import React, { useEffect, useRef, useState } from "react";
import { Input } from "antd";

export default function CodeInput(props) {
    const {
        length = 6,
        disabled = false,
        resetKey = 0,
        onChange = () => { },
        onComplete = () => { },
    } = props;

    const [digits, setDigits] = useState(() =>
        Array.from({ length }, () => "")
    );

    const inputRefs = useRef([]);

    const focusInput = (index, select = false) => {
        const input = inputRefs.current[index];

        if (!input) return;

        input.focus();

        if (select && typeof input.select === "function") {
            input.select();
        }
    };

    useEffect(() => {
        setDigits(Array.from({ length }, () => ""));

        const frame = requestAnimationFrame(() => {
            focusInput(0);
        });

        return () => {
            cancelAnimationFrame(frame);
        };
    }, [length, resetKey]);

    const updateDigits = (nextDigits) => {
        setDigits(nextDigits);

        const code = nextDigits.join("");

        onChange(code);

        if (code.length === length) {
            onComplete(code);
        }
    };

    const handleChange = (index, value) => {
        const numbers = String(value).replace(/\D/g, "");

        if (numbers.length > 1) {
            const nextDigits = Array.from(
                { length },
                (_, i) => numbers[i] || ""
            );

            updateDigits(nextDigits);
            return;
        }

        const nextDigits = [...digits];
        nextDigits[index] = numbers;

        updateDigits(nextDigits);

        if (numbers && index < length - 1) {
            focusInput(index + 1, true);
        }
    };

    const handleKeyDown = (index, event) => {
        if (event.key === "Backspace") {
            if (digits[index]) {
                const nextDigits = [...digits];
                nextDigits[index] = "";

                updateDigits(nextDigits);
                return;
            }

            if (index > 0) {
                const previousIndex = index - 1;
                const nextDigits = [...digits];

                nextDigits[previousIndex] = "";

                updateDigits(nextDigits);
                focusInput(previousIndex);
            }

            return;
        }

        if (event.key === "ArrowLeft" && index > 0) {
            event.preventDefault();
            focusInput(index - 1);
            return;
        }

        if (event.key === "ArrowRight" && index < length - 1) {
            event.preventDefault();
            focusInput(index + 1);
        }
    };

    const handlePaste = (event) => {
        event.preventDefault();

        const pastedCode = event.clipboardData
            .getData("text")
            .replace(/\D/g, "")
            .slice(0, length);

        if (!pastedCode) return;

        const nextDigits = Array.from(
            { length },
            (_, index) => pastedCode[index] || ""
        );

        updateDigits(nextDigits);

        const focusIndex = Math.min(
            pastedCode.length,
            length - 1
        );

        requestAnimationFrame(() => {
            focusInput(focusIndex);
        });
    };

    return (
        <div
            aria-label={`${length} digit recovery code`}
            style={{
                display: "flex",
                justifyContent: "center",
                gap: 8,
            }}
            onPaste={handlePaste}
        >
            {digits.map((digit, index) => (
                <Input
                    key={index}
                    ref={(node) => {
                        inputRefs.current[index] = node;
                    }}
                    value={digit}
                    disabled={disabled}
                    inputMode="numeric"
                    autoComplete={
                        index === 0 ? "one-time-code" : "off"
                    }
                   
                    aria-label={`Recovery code digit ${index + 1}`}
                    onFocus={(event) => {
                        event.currentTarget.select();
                    }}
                    onChange={(event) => {
                        handleChange(index, event.target.value);
                    }}
                    onKeyDown={(event) => {
                        handleKeyDown(index, event);
                    }}
                    style={{
                        width: 44,
                        height: 48,
                        padding: 0,
                        fontSize: 22,
                        textAlign: "center",
                    }}
                />
            ))}
        </div>
    );
}